import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UseQueuedItemPersistenceOptions {
    queueKey?: string;
    onError?: (error: unknown, itemId: string) => void;
}

const sharedQueueTails = new Map<string, Promise<void>>();

/**
 * Keeps optimistic sorting interactive while persistence is pending. Writes
 * are sequenced so an older server response cannot overwrite a newer order,
 * while pending state remains scoped to each moved item.
 */
const useQueuedItemPersistence = ({ onError, queueKey }: UseQueuedItemPersistenceOptions = {}) => {
    const [pendingCounts, setPendingCounts] = useState<ReadonlyMap<string, number>>(new Map());
    const mountedRef = useRef(true);
    const onErrorRef = useRef(onError);
    const queueTailRef = useRef<Promise<void>>(Promise.resolve());

    useLayoutEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const increment = useCallback((itemId: string) => {
        setPendingCounts((current) => {
            const next = new Map(current);

            next.set(itemId, (next.get(itemId) ?? 0) + 1);
            return next;
        });
    }, []);

    const decrement = useCallback((itemId: string) => {
        if (!mountedRef.current) {
            return;
        }

        setPendingCounts((current) => {
            const next = new Map(current);
            const remaining = (next.get(itemId) ?? 1) - 1;

            if (remaining > 0) {
                next.set(itemId, remaining);
            } else {
                next.delete(itemId);
            }

            return next;
        });
    }, []);

    const enqueue = useCallback(
        (itemId: string, persist: () => Promise<unknown>): Promise<void> => {
            increment(itemId);

            const tail = queueKey ? (sharedQueueTails.get(queueKey) ?? Promise.resolve()) : queueTailRef.current;
            const operation = tail.then(async () => {
                await persist();
            });

            const settled = operation.catch(() => undefined);
            if (queueKey) {
                sharedQueueTails.set(queueKey, settled);
                void settled.then(() => {
                    if (sharedQueueTails.get(queueKey) === settled) {
                        sharedQueueTails.delete(queueKey);
                    }
                });
            } else {
                queueTailRef.current = settled;
            }

            return operation
                .catch((error) => {
                    onErrorRef.current?.(error, itemId);
                })
                .finally(() => decrement(itemId));
        },
        [decrement, increment, queueKey],
    );

    const isSyncing = useCallback((itemId: string) => (pendingCounts.get(itemId) ?? 0) > 0, [pendingCounts]);

    return { enqueue, isSyncing, pendingItemIds: new Set(pendingCounts.keys()) };
};

export default useQueuedItemPersistence;
